// App-layer API client with IndexedDB fallbacks.
//
//   fetchJson(path, opts)  network only — used by the sync manager
//   apiGet(path, opts)     network-first; on network failure / timeout / 5xx
//                          answers from the offline snapshot when a fallback
//                          exists for that route (see src/offline/fallbacks.js)
//
// Auth: verifyToken 302s to the login app → `opaqueredirect`. That is a real
// auth failure and must NEVER be answered from the cache; the auth handler
// (OfflineProvider) redirects to login instead.
import { getAPIBaseURL } from "./api";
import { apiFetch } from "../offline/netFetch";
import { resolveFallback } from "../offline/fallbacks";

export class ApiAuthError extends Error {
  constructor(path) { super(`unauthenticated: ${path}`); this.name = "ApiAuthError"; this.path = path; }
}
export class ApiHttpError extends Error {
  constructor(status, path) { super(`HTTP ${status}: ${path}`); this.name = "ApiHttpError"; this.status = status; this.path = path; }
}

let authHandler = null;
export function setAuthErrorHandler(fn) { authHandler = fn; }

const fallbackListeners = new Set();
export function onFallback(fn) { fallbackListeners.add(fn); return () => fallbackListeners.delete(fn); }

export async function fetchJson(path, { timeoutMs = 8000, method = "GET", body, signal } = {}) {
  const base = getAPIBaseURL() || "";
  const url = /^https?:/i.test(path) ? path : `${base}${path}`;
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), timeoutMs);
  if (signal) signal.addEventListener("abort", () => ctrl.abort(), { once: true });
  try {
    const res = await apiFetch(url, {
      method,
      credentials: "include",
      redirect: "manual",
      cache: "no-store",
      headers: body !== undefined ? { "Content-Type": "application/json" } : undefined,
      body: body !== undefined ? JSON.stringify(body) : undefined,
      signal: ctrl.signal,
    });
    if (res.type === "opaqueredirect" || res.status === 401 || res.status === 403) throw new ApiAuthError(path);
    if (!res.ok) throw new ApiHttpError(res.status, path);
    return await res.json();
  } finally {
    clearTimeout(timer);
  }
}

export async function apiGet(path, opts = {}) {
  try {
    return await fetchJson(path, opts);
  } catch (err) {
    if (err instanceof ApiAuthError) { authHandler?.(err); throw err; }
    if (err instanceof ApiHttpError && err.status < 500) throw err;
    const fallback = await resolveFallback(path);
    if (!fallback) throw err;
    const data = await fallback();
    if (data === null || data === undefined) throw err;
    fallbackListeners.forEach((fn) => { try { fn(path); } catch { /* ignore */ } });
    return data;
  }
}
